import type Parse from 'parse';

import { Algorithm, AlgorithmItem, ExecutionEnvironment } from '@telespot/domain';

import { ParseMapper } from '../../parse.mapper';
import { ObjectTopology } from '../../parse.topology';

export enum AlgorithmTopology {
  TABLE = 'Algorithms',
  NAME = 'name',
  VERSION = 'version',
  DESCRIPTION = 'description',
  REGULATED = 'regulated',
  CLOUD_CONFIG = 'cloud_config',
  MOBILE_CONFIG = 'mobile_config',
  APPLICATION = 'application',
  NUM_ORGANIZATIONS = 'numOrganizations',
  NUM_WORKSPACES = 'numWorkspaces',
  DEPRECATED = 'deprecated',
}

export class ParseAlgorithmMapper extends ParseMapper {
  public toDomain(parseObject: Parse.Object): Algorithm {
    const cloudConfig = parseObject.get(AlgorithmTopology.CLOUD_CONFIG);
    const mobileConfig = parseObject.get(AlgorithmTopology.MOBILE_CONFIG);

    const environment = cloudConfig
      ? ExecutionEnvironment.CLOUD
      : mobileConfig
      ? ExecutionEnvironment.MOBILE
      : undefined;

    // FIX: throw on server as validation in beforeSave
    if (!environment) throw new Error('[ParseAlgorithmMapper] Could not determine execution environment');

    return new Algorithm({
      id: parseObject.id,
      name: parseObject.get(AlgorithmTopology.NAME),
      version: parseObject.get(AlgorithmTopology.VERSION),
      description: parseObject.get(AlgorithmTopology.DESCRIPTION),
      regulated: Boolean(parseObject.get(AlgorithmTopology.REGULATED)),
      deprecated: Boolean(parseObject.get(AlgorithmTopology.DEPRECATED)),
      environment,
      config: cloudConfig ?? mobileConfig,
      application: parseObject.get(AlgorithmTopology.APPLICATION),
      createdAt: parseObject.get(ObjectTopology.CREATED_AT),
      updatedAt: parseObject.get(ObjectTopology.UPDATED_AT),
      numOrganizations: parseObject.get(AlgorithmTopology.NUM_ORGANIZATIONS) ?? 0,
      numWorkspaces: parseObject.get(AlgorithmTopology.NUM_WORKSPACES) ?? 0,
    });
  }

  public fromDomain(algorithm: Algorithm): Parse.Object {
    const configKey = algorithm.executableOn(ExecutionEnvironment.CLOUD)
      ? AlgorithmTopology.CLOUD_CONFIG
      : AlgorithmTopology.MOBILE_CONFIG;

    const parseObject = this.parse.Object.fromJSON({
      className: AlgorithmTopology.TABLE,
      [ObjectTopology.ID]: algorithm.id,
      [ObjectTopology.CREATED_AT]: algorithm.createdAt,
      [ObjectTopology.UPDATED_AT]: algorithm.updatedAt,
    });

    parseObject.set({
      [AlgorithmTopology.NAME]: algorithm.name,
      [AlgorithmTopology.VERSION]: algorithm.version,
      [AlgorithmTopology.DESCRIPTION]: algorithm.description,
      [AlgorithmTopology.REGULATED]: algorithm.regulated,
      [AlgorithmTopology.APPLICATION]: algorithm.application,
      [AlgorithmTopology.DEPRECATED]: algorithm.deprecated,
      [configKey]: algorithm.config,
    });

    return parseObject;
  }

  public static toItem(obj: Parse.Object): AlgorithmItem {
    return {
      id: obj.id,
      createdAt: obj.createdAt,
      name: obj.get(AlgorithmTopology.NAME),
      version: obj.get(AlgorithmTopology.VERSION),
      regulated: Boolean(obj.get(AlgorithmTopology.REGULATED)),
      application: obj.get(AlgorithmTopology.APPLICATION) ?? 'unspecified',
      deprecated: Boolean(obj.get(AlgorithmTopology.DEPRECATED)),
      environment: obj.get(AlgorithmTopology.CLOUD_CONFIG) ? ExecutionEnvironment.CLOUD : ExecutionEnvironment.MOBILE,
    };
  }
}
